import { z } from 'zod';
import { PERSONA_REFERENCE_VIDEO_ANALYSIS_PROMPT_USED_IN, PERSONA_SCRIPT_IMPORT_PROMPT_USED_IN } from '../events/persona-opportunities.js';
import { CHANGELOG_PROMPT_DEPLOY_NOTES, CHANGELOG_PROMPT_ROUNDUP } from './changelog-prompts.js';
import {
  SCRIPT_REVIEW_PROMPT_ITERATION_TEXT_OVERLAY,
  SCRIPT_REVIEW_PROMPT_ITERATION_VISUAL,
  SCRIPT_REVIEW_PROMPT_ITERATION_VOICEOVER,
  SCRIPT_REVIEW_PROMPT_SCRIPT_EXTRACTION,
  SCRIPT_REVIEW_PROMPT_SCRIPT_MATCH,
  SCRIPT_REVIEW_PROMPT_SUGGESTION,
  SCRIPT_REVIEW_PROMPT_TEXT_OVERLAY,
  SCRIPT_REVIEW_PROMPT_TRANSCRIPT,
  SCRIPT_REVIEW_PROMPT_VISUAL,
  SCRIPT_REVIEW_PROMPT_VOICEOVER,
} from './script-review-prompts.js';

/**
 * Job a prompt is for — drives which models appear in the Prompts dropdown.
 * - text: text in → text out
 * - vision: video / PDF / image in → text out
 * - image: text in → image out
 */
export const PromptTaskTypeSchema = z.enum(['text', 'vision', 'image']);
export type PromptTaskType = z.infer<typeof PromptTaskTypeSchema>;

/** Prisma / API wire form (uppercase). */
export const PromptTaskTypeDbSchema = z.enum(['TEXT', 'VISION', 'IMAGE']);
export type PromptTaskTypeDb = z.infer<typeof PromptTaskTypeDbSchema>;

export function toPromptTaskTypeDb(taskType: PromptTaskType): PromptTaskTypeDb {
  if (taskType === 'text') return 'TEXT';
  if (taskType === 'vision') return 'VISION';
  return 'IMAGE';
}

export function fromPromptTaskTypeDb(taskType: PromptTaskTypeDb): PromptTaskType {
  if (taskType === 'TEXT') return 'text';
  if (taskType === 'VISION') return 'vision';
  return 'image';
}

const EXCLUDED_SUBSTRINGS = [
  'embed',
  'tts',
  'audio',
  'veo',
  'computer-use',
  'robotics',
] as const;

/**
 * Derive which prompt jobs a model id can serve. Vertex does not expose output
 * modalities, so this is id-heuristic based.
 */
export function derivePromptModelCapabilities(modelId: string): PromptTaskType[] {
  const id = modelId.trim().toLowerCase();
  if (!id) return [];
  if (EXCLUDED_SUBSTRINGS.some((s) => id.includes(s))) return [];

  if (id.includes('-image') || id.includes('image-')) return ['image'];

  if (id.startsWith('claude-')) return ['text'];

  if (id.startsWith('gemini-')) return ['text', 'vision'];

  return [];
}

export function modelSupportsTask(
  modelId: string,
  taskType: PromptTaskType,
): boolean {
  return derivePromptModelCapabilities(modelId).includes(taskType);
}

/**
 * Prefer a saved prompt model when it supports the call site's task.
 * Otherwise return the service fallback (expand/contract).
 */
export function resolvePromptModelForTask(
  promptModel: string | null | undefined,
  taskType: PromptTaskType,
  fallbackModel: string,
): string {
  if (promptModel && modelSupportsTask(promptModel, taskType)) {
    return promptModel;
  }
  return fallbackModel;
}

/**
 * Prefill map for the Prompts editor Task Select.
 * Unknown / custom usedIn values fall back to `text`.
 */
export const PROMPT_TASK_TYPE_BY_USED_IN: Readonly<Record<string, PromptTaskType>> = {
  'creative-storyboard-image': 'image',
  'persona-avatar-generation': 'image',

  [SCRIPT_REVIEW_PROMPT_TRANSCRIPT]: 'vision',
  [SCRIPT_REVIEW_PROMPT_TEXT_OVERLAY]: 'vision',
  [SCRIPT_REVIEW_PROMPT_VISUAL]: 'vision',
  [SCRIPT_REVIEW_PROMPT_SCRIPT_EXTRACTION]: 'vision',
  [SCRIPT_REVIEW_PROMPT_SCRIPT_MATCH]: 'vision',
  [PERSONA_REFERENCE_VIDEO_ANALYSIS_PROMPT_USED_IN]: 'vision',
  [SCRIPT_REVIEW_PROMPT_ITERATION_VOICEOVER]: 'vision',
  [SCRIPT_REVIEW_PROMPT_ITERATION_TEXT_OVERLAY]: 'vision',
  [SCRIPT_REVIEW_PROMPT_ITERATION_VISUAL]: 'vision',

  // Explicit text keys (also the default for unlisted usedIn).
  'opportunity-generation-premise': 'text',
  'opportunity-generation-framework': 'text',
  'creative-script-generation-angles': 'text',
  'creative-script-generation-topics': 'text',
  'creative-script-generation-custom': 'text',
  'creative-brief-generation-persona-angles': 'text',
  'creative-brief-edit-persona-angles': 'text',
  'creative-script-hook-variations-persona': 'text',
  [PERSONA_SCRIPT_IMPORT_PROMPT_USED_IN]: 'text',
  'creative-script-edit-meta-prompt': 'text',
  'creative-script-edit-feedback-message': 'text',
  'creative-storyboard-beat-extraction': 'text',
  [SCRIPT_REVIEW_PROMPT_VOICEOVER]: 'text',
  [SCRIPT_REVIEW_PROMPT_SUGGESTION]: 'text',
  [CHANGELOG_PROMPT_DEPLOY_NOTES]: 'text',
  [CHANGELOG_PROMPT_ROUNDUP]: 'text',
};

export function getPromptTaskTypeForUsedIn(usedIn: string): PromptTaskType {
  return PROMPT_TASK_TYPE_BY_USED_IN[usedIn] ?? 'text';
}
